import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AppStack from '../../common/AppStack';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import AppText from '../../common/AppText';
import { triggerNotification } from '../../../helpers/notification';
import ResourceSelectModal from '../../common/ResourceSelectModal';

const DeleteMediaModal = ({
  isOpen,
  onClose,
  fkEcomVendor,
  fkEcomStore,
  media,
  filter,
  totalItemCount,
  onSuccess
}) => {
  const [formState, setFormState] = useState({
    allEcomStoreMedia: [],
    selectType: '',
    fetching: false,
    loading: false
  });
  const { fetchVendorMedia, deleteVendorMedia } = useContext(VendorContext);
  const { fetchStoreMedia, deleteStoreMedia } = useContext(StoreContext);
  const mediaToDelete =
    (formState.selectType === 'all' ? formState.allEcomStoreMedia : media) ||
    [];

  useEffect(() => {
    if (isOpen) {
      setFormState({
        allEcomStoreMedia: [],
        selectType: '',
        fetching: false,
        loading: false
      });
    }
  }, [isOpen]);

  const onSuccessCallback = () => {
    triggerNotification(`Media deleted.`, 'success');
    onClose();
    if (onSuccess) {
      onSuccess();
    }
  };

  const onErrorCallback = (e) => {
    setFormState({ ...formState, loading: false });
    triggerNotification(e);
  };

  return (
    <ResourceSelectModal
      filter={filter}
      formSectionProps={{
        onCancel: onClose,
        isLoading: formState.loading,
        onSubmit: () => {
          setFormState({ ...formState, loading: true });
          if (fkEcomVendor) {
            const mediaIds = mediaToDelete.map((p) => p.pkEcomVendorMedia);

            deleteVendorMedia(
              fkEcomVendor,
              mediaIds,
              onSuccessCallback,
              onErrorCallback
            );
          }
          else {
            const mediaIds = mediaToDelete.map((p) => p.pkEcomStoreMedia);

            deleteStoreMedia(
              fkEcomStore,
              mediaIds,
              onSuccessCallback,
              onErrorCallback
            );
          }
        },
        submitTitle: `Yes, delete media`
      }}
      isLoading={formState.fetching}
      isOpen={isOpen}
      onClose={onClose}
      onSelect={(selectType) => {
        setFormState({ ...formState, selectType });
        if (selectType === 'all') {
          setFormState({ ...formState, selectType, fetching: true });
          if (fkEcomVendor) {
            fetchVendorMedia(
              { fkEcomVendor, ...filter, skipStateUpdate: true },
              (data) => {
                setFormState({
                  ...formState,
                  selectType,
                  allEcomStoreMedia: data,
                  fetching: false
                });
              },
              triggerNotification
            );
          }
          else {
            fetchStoreMedia(
              { fkEcomStore, ...filter, skipStateUpdate: true },
              (data) => {
                setFormState({
                  ...formState,
                  selectType,
                  allEcomStoreMedia: data,
                  fetching: false
                });
              },
              triggerNotification
            );
          }
        }
      }}
      plural="media"
      selectedIds={mediaToDelete.map(
        (p) => p.pkEcomStoreMedia || p.pkEcomVendorMedia
      )}
      selectLabel="Delete"
      selectLabelDescription="Select the media files you want to delete."
      singular="media"
      size={450}
      title="Delete media files?"
      totalItemCount={totalItemCount}
    >
      <AppStack style={{ gap: 8 }}>
        <AppStack style={{ gap: 10 }}>
          <AppText style={{ fontSize: 14 }}>
            Are you sure you want to delete{' '}
            <span style={{ fontWeight: 500 }}>
              {mediaToDelete?.length === 1
                ? mediaToDelete[0].alt
                : `${mediaToDelete?.length} media files`}
            </span>
            ?
          </AppText>
          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
            This action cannot be undone.
          </AppText>
        </AppStack>
      </AppStack>
    </ResourceSelectModal>
  );
};

DeleteMediaModal.propTypes = {
  filter: PropTypes.object,
  fkEcomStore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fkEcomVendor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOpen: PropTypes.bool,
  media: PropTypes.array,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  totalItemCount: PropTypes.number
};

export default DeleteMediaModal;
